import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api, getFullApiUrl } from '../services/api';
import { FiX, FiLoader, FiClock, FiImage, FiRotateCcw, FiChevronLeft, FiChevronRight, FiCheck, FiTrash2, FiAlertTriangle } from 'react-icons/fi';

function VersionHistoryModal({ content, onClose, onRestore, onRefresh }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Fetch version history
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        setLoading(true);
        const data = await api.getContentVersions(content.id);
        setVersions(data.versions || []);
        // Select current version by default
        const current = data.versions?.find(v => v.is_current);
        if (current) {
          setSelectedVersion(current);
        }
      } catch (err) {
        console.error('Failed to fetch versions:', err);
        setError('Failed to load version history');
      } finally {
        setLoading(false);
      }
    };

    fetchVersions();
  }, [content.id]);

  // Reset image index when version changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedVersion]);

  // Get images for selected version
  const getVersionImages = (version) => {
    if (!version) return [];
    const images = [];
    if (version.image_url) images.push(getFullApiUrl(version.image_url));
    if (version.images && Array.isArray(version.images)) {
      version.images.forEach(img => {
        const fullUrl = getFullApiUrl(img);
        if (fullUrl && !images.includes(fullUrl)) images.push(fullUrl);
      });
    }
    return images;
  };

  const selectedImages = getVersionImages(selectedVersion);

  const handleRestore = async (versionNumber) => {
    if (restoring) return;
    
    setRestoring(true);
    setError('');

    try {
      await api.restoreContentVersion(content.id, versionNumber);
      onRestore();
    } catch (err) {
      console.error('Failed to restore version:', err);
      setError(err.response?.data?.detail || 'Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  const handleDelete = async (versionNumber) => {
    if (deleting) return;
    
    setDeleting(true);
    setError('');

    try {
      const response = await api.deleteContentVersion(content.id, versionNumber);
      
      // Reload versions from server since version numbers have been renumbered
      const versionsResponse = await api.getContentVersions(content.id);
      if (versionsResponse && versionsResponse.versions) {
        setVersions(versionsResponse.versions);
        
        // After deletion/renumbering, always select the current version
        // This avoids sync issues where selectedVersion had an old version number
        const current = versionsResponse.versions.find(v => v.is_current);
        setSelectedVersion(current || null);
        
        // Notify parent component about the version change
        if (onRefresh) {
          onRefresh();
        }
      }
      
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete version:', err);
      setError(err.response?.data?.detail || 'Failed to delete version');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rounded-xl w-full max-w-5xl shadow-2xl border border-border/50 flex flex-col"
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-5 border-b border-border/50">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <FiClock className="text-primary" />
              Version History
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {versions.length} version{versions.length !== 1 ? 's' : ''} · Browse and restore previous versions
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="close-version-modal">
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Delete Confirmation Dialog */}
          {deleteConfirm && (
            <div className="absolute inset-0 bg-black/50 z-10 flex items-center justify-center p-4">
              <div className="bg-card rounded-lg p-5 max-w-sm border border-border shadow-xl">
                <div className="flex items-center gap-3 text-amber-400 mb-3">
                  <FiAlertTriangle size={24} />
                  <h3 className="font-semibold text-lg">Delete Version {deleteConfirm}?</h3>
                </div>
                <p className="text-muted-foreground text-sm mb-4">
                  This will permanently delete this version from history. This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleDelete(deleteConfirm)}
                    disabled={deleting}
                    className="flex-1 flex items-center justify-center gap-2 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {deleting ? (
                      <FiLoader className="animate-spin" size={16} />
                    ) : (
                      <FiTrash2 size={16} />
                    )}
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deleting}
                    className="flex-1 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <FiLoader className="animate-spin text-primary" size={32} />
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : versions.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-muted-foreground">No version history available</p>
            </div>
          ) : (
            <>
              {/* Version List - Left Side */}
              <div className="w-64 border-r border-border/50 overflow-y-auto">
                <div className="p-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">All Versions</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    {versions.length} / 6 slots used
                  </p>
                  <div className="space-y-2">
                    {versions.slice().reverse().map((version) => (
                      <div
                        key={version.version}
                        className={`relative group rounded-lg border transition-all ${
                          selectedVersion?.version === version.version
                            ? 'bg-primary/10 border-primary/50'
                            : 'bg-background/50 border-border/30 hover:border-border'
                        }`}
                      >
                        <button
                          onClick={() => setSelectedVersion(version)}
                          className="w-full text-left p-3"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground">
                              Version {version.version}
                            </span>
                            {version.is_current && (
                              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(version.created_at)}
                          </p>
                          {/* Show which image was refined if available */}
                          {version.refined_image_label && (
                            <p className="text-xs text-primary/70 mt-1">
                              Refined: {version.refined_image_label}
                            </p>
                          )}
                          {version.refinement_note && (
                            <p className="text-xs text-muted-foreground mt-1 truncate">
                              {version.refinement_note}
                            </p>
                          )}
                        </button>
                        
                        {/* Delete button - only show for non-current versions */}
                        {!version.is_current && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(version.version);
                            }}
                            className="absolute top-2 right-2 p-1.5 rounded-md bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                            title="Delete this version"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Version Preview - Right Side */}
              <div className="flex-1 overflow-y-auto p-5">
                {selectedVersion && (
                  <div className="space-y-5">
                    {/* Image Gallery */}
                    {selectedImages.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                            <FiImage size={14} />
                            Images ({selectedImages.length})
                          </p>
                          {selectedImages.length > 1 && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setCurrentImageIndex(prev => Math.max(0, prev - 1))}
                                disabled={currentImageIndex === 0}
                                className="p-1 rounded hover:bg-muted disabled:opacity-30"
                              >
                                <FiChevronLeft size={18} />
                              </button>
                              <span className="text-sm text-muted-foreground">
                                {currentImageIndex + 1} / {selectedImages.length}
                              </span>
                              <button
                                onClick={() => setCurrentImageIndex(prev => Math.min(selectedImages.length - 1, prev + 1))}
                                disabled={currentImageIndex === selectedImages.length - 1}
                                className="p-1 rounded hover:bg-muted disabled:opacity-30"
                              >
                                <FiChevronRight size={18} />
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Main Image */}
                        <div className="relative aspect-square max-w-md mx-auto bg-muted rounded-lg overflow-hidden">
                          <img
                            src={selectedImages[currentImageIndex]}
                            alt={`Version ${selectedVersion.version}`}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Thumbnail Strip */}
                        {selectedImages.length > 1 && (
                          <div className="flex gap-2 justify-center flex-wrap">
                            {selectedImages.map((img, idx) => (
                              <button
                                key={idx}
                                onClick={() => setCurrentImageIndex(idx)}
                                className={`w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                                  currentImageIndex === idx
                                    ? 'border-primary'
                                    : 'border-transparent opacity-60 hover:opacity-100'
                                }`}
                              >
                                <img src={img} alt="" className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Text Content */}
                    <div className="space-y-4">
                      {selectedVersion.hook && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Hook</p>
                          <p className="text-foreground">{selectedVersion.hook}</p>
                        </div>
                      )}

                      {selectedVersion.caption && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Caption</p>
                          <p className="text-foreground whitespace-pre-wrap">{selectedVersion.caption}</p>
                        </div>
                      )}

                      {selectedVersion.visual_direction && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Visual Direction</p>
                          <p className="text-muted-foreground text-sm">{selectedVersion.visual_direction}</p>
                        </div>
                      )}

                      {selectedVersion.cta && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">CTA</p>
                          <p className="text-foreground">{selectedVersion.cta}</p>
                        </div>
                      )}

                      {selectedVersion.hashtags && selectedVersion.hashtags.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Hashtags</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedVersion.hashtags.map((tag, idx) => (
                              <span key={idx} className="text-xs bg-muted px-2 py-1 rounded text-muted-foreground">
                                {tag.startsWith('#') ? tag : `#${tag}`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Restore Button */}
                    {!selectedVersion.is_current && (
                      <div className="pt-4 border-t border-border/50">
                        <button
                          onClick={() => handleRestore(selectedVersion.version)}
                          disabled={restoring}
                          className="w-full flex items-center justify-center gap-2 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                          {restoring ? (
                            <>
                              <FiLoader className="animate-spin" size={18} />
                              Restoring...
                            </>
                          ) : (
                            <>
                              <FiRotateCcw size={18} />
                              Restore This Version
                            </>
                          )}
                        </button>
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          This will create a new version with this content
                        </p>
                      </div>
                    )}

                    {selectedVersion.is_current && (
                      <div className="pt-4 border-t border-border/50">
                        <div className="flex items-center justify-center gap-2 py-3 bg-muted/50 text-muted-foreground rounded-lg">
                          <FiCheck size={18} />
                          This is the current version
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default VersionHistoryModal;
